import { UploadOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Segmented, Select } from 'antd';
import { FormikProps } from 'formik';
import React from 'react';
import { ErrorAwareLabel } from '../../../components/error-aware-label';
import { Section } from '../../../components/form-fields/section';
import { NotificationManager } from '../../../components/notification-manager';
import { useContestStore } from '../../../contexts/mobx';
import { FieldError } from '../general-tab/field-error';
import { fontStack, missingOn, systemFontOf, SYSTEM_FONTS } from './fonts';
import { FontPlace, ThemeColor, ThemeFormValues } from './form';
import style from './style.module.scss';

/** Each colour by where it shows, in the order the eye meets them. */
const COLORS: readonly {
    key: ThemeColor;
    label: string;
    help: string;
}[] = [
    {
        key: 'navBar',
        label: 'Navigation bars',
        help:
            'The bar at the top of the contest and the tab bar at the bottom.',
    },
    {
        key: 'navBarText',
        label: 'Navigation bar text',
        help: 'Text and icons on those two bars.',
    },
    {
        key: 'pageBackground',
        label: 'Page background',
        help: 'Behind the blocks of every page.',
    },
    {
        key: 'cardBackground',
        label: 'Block background',
        help: 'Blocks, forms and dialogs.',
    },
    {
        key: 'bodyText',
        label: 'Text',
        help: 'Titles and text of the pages and blocks.',
    },
    {
        key: 'mutedText',
        label: 'Secondary text',
        help: 'Hints under fields, captions and placeholders.',
    },
    {
        key: 'buttonBackground',
        label: 'Buttons',
        help:
            'Buttons, ticked boxes and the outline of the field being typed in.',
    },
    {
        key: 'buttonText',
        label: 'Button text',
        help: 'Text on the buttons.',
    },
    {
        key: 'highlightText',
        label: 'Highlighted text',
        help: 'The highlight line of a block, and links to the policies.',
    },
];

const FONT_PLACES: readonly { key: FontPlace; label: string }[] = [
    { key: 'titles', label: 'Titles' },
    { key: 'body', label: 'Text' },
];

const MAX_FONT_BYTES = 5 * 1024 * 1024;
const FONT_LOAD_TIMEOUT_MS = 10000;
const SAMPLE = 'Gagnez un an d’essence · Win a year of gas';

let sampleFaces = 0;

/**
 * Loads an uploaded font in the CMS under a name of its own, to show a
 * sample in it. `failed` once the file is not a font the browser can read.
 */
function useUploadedFont(
    url: string | null
): { family: string | null; failed: boolean } {
    const [loaded, setLoaded] = React.useState<{
        url: string;
        family: string | null;
    } | null>(null);

    React.useEffect(() => {
        if (!url || typeof FontFace === 'undefined') {
            return;
        }
        let isCurrent = true;
        sampleFaces += 1;
        const family = `contest-theme-sample-${sampleFaces}`;
        const face = new FontFace(family, `url(${JSON.stringify(url)})`);
        document.fonts.add(face);
        let timer: number | undefined;
        const timeout = new Promise<never>((_resolve, reject) => {
            timer = window.setTimeout(reject, FONT_LOAD_TIMEOUT_MS);
        });
        Promise.race([face.load(), timeout]).then(
            () => isCurrent && setLoaded({ url, family }),
            () => isCurrent && setLoaded({ url, family: null })
        );
        return () => {
            isCurrent = false;
            window.clearTimeout(timer);
            document.fonts.delete(face);
        };
    }, [url]);

    if (!url || loaded?.url !== url) {
        return { family: null, failed: false };
    }
    return { family: loaded.family, failed: loaded.family === null };
}

/** A woff2 file starts with its signature, whatever its name says. */
async function isWoff2(file: File): Promise<boolean> {
    const signature = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return String.fromCharCode(...signature) === 'wOF2';
}

/** The file's name without its extension, as a font name may be written. */
function familyFromFileName(name: string): string {
    return name
        .replace(/\.woff2$/i, '')
        .replace(/[^\p{L}\p{N} _-]+/gu, ' ')
        .trim()
        .slice(0, 100);
}

interface FontSelectProps {
    value: string;
    onChange: (font: { family: string; fallback: string }) => void;
    /** Shown first when the value is outside the list. */
    current?: { family: string; fallback: string };
}

/** The list of fonts, each name written in its own face. */
const FontSelect = ({ value, onChange, current }: FontSelectProps) => {
    const outsideList =
        current && !systemFontOf(current.family) ? [current] : [];
    const options = [
        ...outsideList.map((font) => ({
            family: font.family,
            fallback: font.fallback,
            note: 'Not in the list',
        })),
        ...SYSTEM_FONTS.map((font) => ({
            family: font.family,
            fallback: font.fallback,
            note: missingOn(font),
        })),
    ];
    return (
        <Select
            showSearch
            className={style.font_select}
            value={value}
            optionFilterProp="search"
            onChange={(family: string) => {
                const font = options.find((each) => each.family === family);
                if (font) {
                    onChange({ family: font.family, fallback: font.fallback });
                }
            }}
            options={options.map((font) => ({
                value: font.family,
                search: font.family,
                label: (
                    <span className={style.font_option}>
                        <span style={{ fontFamily: fontStack(font) }}>
                            {font.family}
                        </span>
                        {font.note && (
                            <span className={style.font_note}>{font.note}</span>
                        )}
                    </span>
                ),
            }))}
        />
    );
};

interface FontFieldProps {
    place: FontPlace;
    label: string;
    formik: FormikProps<ThemeFormValues>;
    errorOf: (path: string) => string | undefined;
}

/** One font, from the list or uploaded, with a sample of it. */
const FontField = ({ place, label, formik, errorOf }: FontFieldProps) => {
    const store = useContestStore();
    const { setFieldValue } = formik;
    const font = formik.values.fonts[place];
    const path = `fonts.${place}`;
    const hiddenInput = React.useRef<HTMLInputElement | null>(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const uploaded = useUploadedFont(
        font.source === 'upload' ? font.upload.url : null
    );
    // The initial value, kept so a family outside the list stays selectable.
    const [initialSystem] = React.useState(font.system);

    const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        if (file.size > MAX_FONT_BYTES) {
            NotificationManager.showError('A font file takes at most 5 MB.');
            return;
        }
        if (!(await isWoff2(file))) {
            NotificationManager.showError(
                'This file is not a woff2 font. Convert it to woff2 first.'
            );
            return;
        }
        setIsUploading(true);
        try {
            const url = await store.uploadFont(file);
            if (url) {
                setFieldValue(`${path}.upload.url`, url);
                if (!font.upload.family.trim()) {
                    setFieldValue(
                        `${path}.upload.family`,
                        familyFromFileName(file.name)
                    );
                }
            }
        } finally {
            setIsUploading(false);
        }
    };

    const sampleFamily =
        font.source === 'system'
            ? fontStack(font.system)
            : uploaded.family
            ? `"${uploaded.family}", ${font.upload.fallback}`
            : font.upload.fallback;

    return (
        <div className={style.font}>
            <div className={style.font_header}>
                <span className="body_bold">{label}</span>
                <Segmented
                    value={font.source}
                    onChange={(value) => setFieldValue(`${path}.source`, value)}
                    options={[
                        { label: 'From the list', value: 'system' },
                        { label: 'Uploaded file', value: 'upload' },
                    ]}
                />
            </div>

            {font.source === 'system' ? (
                <FontSelect
                    value={font.system.family}
                    current={initialSystem}
                    onChange={(picked) =>
                        setFieldValue(`${path}.system`, picked)
                    }
                />
            ) : (
                <div className={style.upload}>
                    <div className={style.upload_file}>
                        <ErrorAwareLabel
                            text="File"
                            hasError={!!errorOf(`${path}.upload.url`)}
                        />
                        <input
                            ref={hiddenInput}
                            type="file"
                            accept=".woff2,font/woff2"
                            hidden
                            onChange={onFileSelected}
                        />
                        <Button
                            icon={<UploadOutlined />}
                            loading={isUploading}
                            onClick={() => hiddenInput.current?.click()}
                        >
                            {font.upload.url
                                ? 'Replace the woff2 file'
                                : 'Upload a woff2 file'}
                        </Button>
                        <FieldError error={errorOf(`${path}.upload.url`)} />
                    </div>
                    <div className={style.upload_field}>
                        <ErrorAwareLabel
                            text="Name"
                            hasError={!!errorOf(`${path}.upload.family`)}
                        />
                        <Input
                            maxLength={100}
                            aria-invalid={!!errorOf(`${path}.upload.family`)}
                            value={font.upload.family}
                            onChange={(e) =>
                                setFieldValue(
                                    `${path}.upload.family`,
                                    e.target.value
                                )
                            }
                        />
                        <FieldError error={errorOf(`${path}.upload.family`)} />
                    </div>
                    <div className={style.upload_field}>
                        <ErrorAwareLabel text="Shown until it loads" />
                        <Select
                            className={style.font_select}
                            value={font.upload.fallback}
                            onChange={(fallback: string) =>
                                setFieldValue(
                                    `${path}.upload.fallback`,
                                    fallback
                                )
                            }
                            options={SYSTEM_FONTS.map((each) => ({
                                value: fontStack(each),
                                label: (
                                    <span
                                        style={{ fontFamily: fontStack(each) }}
                                    >
                                        {each.family}
                                    </span>
                                ),
                            }))}
                        />
                    </div>
                </div>
            )}

            {/* An upload shows in its fallback until it has loaded. */}
            <p className={style.sample} style={{ fontFamily: sampleFamily }}>
                {SAMPLE}
            </p>
            {uploaded.failed && (
                <span className={style.error}>
                    This file could not be read as a font. Upload another one.
                </span>
            )}
        </div>
    );
};

interface ThemeTabProps {
    formik: FormikProps<ThemeFormValues>;
}

/**
 * The contest's colours, named by where they show on the screen, its two
 * fonts and its corner radii. The preview shows them once saved.
 */
export const ThemeTab = ({ formik }: ThemeTabProps) => {
    const { values, setFieldValue } = formik;
    const errorOf = (path: string) =>
        formik.submitCount > 0 ? formik.getFieldMeta(path).error : undefined;

    return (
        <div className={style.theme_tab}>
            <Section title="Colours">
                <p className="footnote_description">
                    Each colour is named by where it shows in the contest.
                </p>
                <div className={style.colors}>
                    {COLORS.map(({ key, label, help }) => {
                        const path = `colors.${key}`;
                        const value = values.colors[key];
                        return (
                            <div key={key} className={style.color}>
                                <ErrorAwareLabel
                                    text={label}
                                    hasError={!!errorOf(path)}
                                />
                                <div className={style.color_inputs}>
                                    <input
                                        type="color"
                                        className={style.swatch}
                                        aria-label={`${label}, picker`}
                                        // The picker shows black for what it cannot read.
                                        value={
                                            /^#[0-9a-f]{6}$/i.test(value)
                                                ? value
                                                : '#000000'
                                        }
                                        onChange={(e) =>
                                            setFieldValue(path, e.target.value)
                                        }
                                    />
                                    <Input
                                        className={style.hex}
                                        aria-label={label}
                                        aria-invalid={!!errorOf(path)}
                                        maxLength={7}
                                        value={value}
                                        onChange={(e) =>
                                            setFieldValue(
                                                path,
                                                e.target.value.trim()
                                            )
                                        }
                                    />
                                </div>
                                <span className="footnote_description">
                                    {help}
                                </span>
                                <FieldError error={errorOf(path)} />
                            </div>
                        );
                    })}
                </div>
            </Section>

            <Section title="Fonts">
                <p className="footnote_description">
                    No font of the list is on every phone. Where a device lacks
                    one, it shows the closest font it has. Upload a woff2 file
                    for the contest to look the same everywhere.
                </p>
                {FONT_PLACES.map(({ key, label }) => (
                    <FontField
                        key={key}
                        place={key}
                        label={label}
                        formik={formik}
                        errorOf={errorOf}
                    />
                ))}
            </Section>

            <Section title="Corners">
                <div className={style.radii}>
                    <div className={style.radius}>
                        <ErrorAwareLabel
                            text="Blocks"
                            hasError={!!errorOf('radius.cards')}
                        />
                        <InputNumber
                            min={0}
                            max={999}
                            precision={0}
                            addonAfter="px"
                            value={values.radius.cards}
                            onChange={(value) =>
                                setFieldValue('radius.cards', value)
                            }
                        />
                        <FieldError error={errorOf('radius.cards')} />
                    </div>
                    <div className={style.radius}>
                        <ErrorAwareLabel
                            text="Buttons"
                            hasError={!!errorOf('radius.buttons')}
                        />
                        <InputNumber
                            min={0}
                            max={999}
                            precision={0}
                            addonAfter="px"
                            value={values.radius.buttons}
                            onChange={(value) =>
                                setFieldValue('radius.buttons', value)
                            }
                        />
                        <FieldError error={errorOf('radius.buttons')} />
                    </div>
                </div>
                <p className="footnote_description">
                    0 gives square corners; 999 rounds the ends fully.
                </p>
            </Section>
        </div>
    );
};
